import { useCallback, useRef } from 'react';

interface QueueItem<R> {
  id: string;
  request: (abortSignal: AbortSignal) => Promise<R>;
  resolve: (value: R) => void;
  reject: (error: any) => void;
  abortController: AbortController;
}

interface UseRequestQueueOptions {
  maxConcurrent?: number;
}

export function useRequestQueue<R>({
  maxConcurrent = 5,
}: UseRequestQueueOptions = {}) {
  const queueRef = useRef<QueueItem<R>[]>([]);
  const runningRef = useRef<Map<string, AbortController>>(new Map());
  const cancelledRef = useRef<Set<string>>(new Set());

  const processQueue = useCallback(() => {
    // If we're at max capacity or no items in queue, return
    if (
      runningRef.current.size >= maxConcurrent ||
      queueRef.current.length === 0
    ) {
      return;
    }

    // Get next item from queue
    const item = queueRef.current.shift();
    if (!item) return;

    // Check if this request was cancelled
    if (cancelledRef.current.has(item.id)) {
      cancelledRef.current.delete(item.id);
      item.reject(new Error('Request cancelled'));
      // Process next item in queue
      processQueue();
      return;
    }

    // Add to running set with abort controller
    runningRef.current.set(item.id, item.abortController);

    // Execute the request with abort signal
    item
      .request(item.abortController.signal)
      .then((result) => {
        // Check if cancelled during execution
        if (cancelledRef.current.has(item.id)) {
          cancelledRef.current.delete(item.id);
          item.reject(new Error('Request cancelled'));
        } else {
          item.resolve(result);
        }
      })
      .catch((error) => {
        // Handle abort error specifically
        if (
          error.name === 'AbortError' ||
          error.message === 'Request cancelled'
        ) {
          item.reject(new Error('Request cancelled'));
        } else if (!cancelledRef.current.has(item.id)) {
          item.reject(error);
        } else {
          cancelledRef.current.delete(item.id);
        }
      })
      .finally(() => {
        // Remove from running set
        runningRef.current.delete(item.id);
        // Process next item in queue
        processQueue();
      });

    // Try to process more items if we still have capacity
    if (runningRef.current.size < maxConcurrent) {
      processQueue();
    }
  }, [maxConcurrent]);

  const addToQueue = useCallback(
    (
      id: string,
      request: (abortSignal: AbortSignal) => Promise<R>,
    ): Promise<R> => {
      return new Promise<R>((resolve, reject) => {
        // Create abort controller for this request
        const abortController = new AbortController();

        // Add item to queue
        queueRef.current.push({
          id,
          request,
          resolve,
          reject,
          abortController,
        });

        // Process the queue
        processQueue();
      });
    },
    [processQueue],
  );

  const cancelRequest = useCallback((id: string) => {
    // Mark as cancelled
    cancelledRef.current.add(id);

    // Abort running request if it exists
    const abortController = runningRef.current.get(id);
    if (abortController) {
      abortController.abort();
    }

    // Remove from queue if it's still pending and abort its controller
    queueRef.current = queueRef.current.filter((item) => {
      if (item.id === id) {
        item.abortController.abort();
        return false;
      }
      return true;
    });
  }, []);

  const clearQueue = useCallback(() => {
    // Cancel all pending requests
    queueRef.current.forEach((item) => {
      cancelledRef.current.add(item.id);
      item.abortController.abort();
    });

    // Cancel all running requests
    runningRef.current.forEach((abortController, id) => {
      cancelledRef.current.add(id);
      abortController.abort();
    });

    // Clear the queue and running requests
    queueRef.current = [];
    runningRef.current.clear();
  }, []);

  const getQueueStatus = useCallback(() => {
    return {
      queueLength: queueRef.current.length,
      runningCount: runningRef.current.size,
      isAtCapacity: runningRef.current.size >= maxConcurrent,
      hasActiveRequests:
        queueRef.current.length > 0 || runningRef.current.size > 0,
      totalPendingRequests: queueRef.current.length + runningRef.current.size,
    };
  }, [maxConcurrent]);

  return {
    addToQueue,
    cancelRequest,
    clearQueue,
    getQueueStatus,
  };
}
